import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { interp } from "@/i18n/translations";

// Small stack of face-down cards; thickness grows with the number of cards.
const Stack = ({ count, size = "md", glow = false, dim = false }) => {
  const layers = Math.min(7, Math.max(count > 0 ? 1 : 0, Math.ceil(count / 12)));
  const dims = size === "lg" ? "w-32 h-52 md:w-40 md:h-64" : "w-24 h-40 md:w-28 md:h-44";
  return (
    <div className={`relative ${dims}`}>
      {count === 0 && (
        <div className="absolute inset-0 rounded-md border border-dashed border-gold/30 flex items-center justify-center">
          <span className="rune text-2xl opacity-40">✷</span>
        </div>
      )}
      {Array.from({ length: layers }).map((_, i) => (
        <div
          key={i}
          style={{ transform: `translate(${i * 2}px, ${-i * 2}px)` }}
          className={`absolute inset-0 card-back rounded-md border border-gold/30 shadow-[0_12px_30px_-12px_rgba(20,16,58,0.35)] ${
            dim ? "opacity-80" : ""
          } ${glow && i === layers - 1 ? "ring-2 ring-gold/60 ring-offset-2 ring-offset-[#F4EAD1]" : ""}`}
        >
          <div className="absolute inset-2 border border-gold/25 rounded-sm flex items-center justify-center">
            <div className="rune text-2xl md:text-3xl opacity-70">✶</div>
          </div>
          <div className="absolute inset-4 border border-gold/15 rounded-sm" />
        </div>
      ))}
    </div>
  );
};

/**
 * DeckCutter — the consultant cuts the deck 5 times. Each cut moves the top portion
 * to the "cut aside" pile and sends the newly exposed card to the cross.
 */
export const DeckCutter = ({ remaining, cutAside, cuts, cutting, onCut, disabled }) => {
  const { t } = useLanguage();
  const canCut = !disabled && !cutting && cuts < 5 && remaining > 1;
  const btnRef = useRef(null);

  // Keep the cursor on the deck so Space / Enter cut it, cut after cut.
  useEffect(() => {
    if (canCut) btnRef.current?.focus({ preventScroll: true });
  }, [canCut, cuts]);

  return (
    <div data-testid="reading-deck-cutter" className="rounded-md border border-gold/25 bg-oxblood-surface/50 p-5 md:p-7">
      <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
        {/* Main deck — click to cut */}
        <div className="flex flex-col items-center gap-3">
          <span className="text-[0.65rem] md:text-[0.7rem] tracking-[0.32em] uppercase text-gold font-sans whitespace-nowrap">
            {interp(t.reading.deckLabel, { n: remaining })}
          </span>
          <motion.button
            ref={btnRef}
            type="button"
            onClick={canCut ? (e) => { if (e.detail !== 0) onCut(); } : undefined}
            disabled={!canCut}
            data-testid="reading-deck-cut-btn"
            aria-label={t.reading.cutAction}
            whileHover={canCut ? { y: -6 } : {}}
            whileTap={canCut ? { scale: 0.97 } : {}}
            className={`relative mt-3 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-4 focus-visible:ring-offset-[#F4EAD1] ${canCut ? "cursor-pointer" : "cursor-default"}`}
          >
            <Stack count={remaining} size="lg" glow={canCut} />
            <AnimatePresence>
              {cutting && (
                <motion.div
                  key={`chunk-${cuts}`}
                  initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
                  animate={{ x: 190, y: 12, rotate: 8, opacity: 0.2 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 pointer-events-none"
                >
                  <Stack count={24} size="lg" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
          <span className={`text-[0.7rem] tracking-[0.3em] uppercase font-bold ${canCut ? "text-gold" : "text-ivory/40"}`}>
            {cuts < 5 ? `✧ ${t.reading.cutAction}` : t.reading.cutsDone}
          </span>
        </div>

        <div className="hidden md:block text-gold/60 text-3xl">→</div>

        {/* Cut-aside pile */}
        <div className="flex flex-col items-center gap-3" data-testid="reading-cut-pile">
          <span className="text-[0.65rem] md:text-[0.7rem] tracking-[0.32em] uppercase text-ivory/60 font-sans whitespace-nowrap">
            {interp(t.reading.cutPileLabel, { n: cutAside })}
          </span>
          <div className="mt-3"><Stack count={cutAside} size="lg" dim /></div>
          <span className="text-[0.7rem] tracking-[0.3em] uppercase text-ivory/70" data-testid="reading-cuts-count">
            {interp(t.reading.cutsOf, { a: cuts })}
          </span>
        </div>
      </div>
      <p className="mt-5 text-center text-ivory/70 font-serif italic text-sm md:text-base">{t.reading.cutHint}</p>
      {cuts < 5 && (
        <p className="mt-2 text-center text-[0.68rem] tracking-[0.3em] uppercase text-gold/80" data-testid="reading-cut-key-hint">
          {t.reading.cutKeyHint}
        </p>
      )}
    </div>
  );
};

export default DeckCutter;
